import React from 'react';
import MuiClose from '@material-ui/icons/Close';
import MuiChevronLeft from '@material-ui/icons/ChevronLeft';
import MuiChevronRight from '@material-ui/icons/ChevronRight';
import uuid from 'uuid';
import { IDealWithEvaluation } from 'store/deal-comparison/types';
import { generateContractPeriodString, calculateNumOfMonths } from 'lib/helpers/date-range';
import routes from 'router/routes';
import { IDealProduct } from 'store/field-options/types';
import { IDealDataSelector } from 'store/deals/types';
import {
  optionsValueHeaderConst,
  pricingFormulaHeaderConst,
  volumeLabelHeaderConst,
  currencyDollarHeaderConst,
  totalRevenueHeaderConst,
  totalNetbackHeaderConst,
  totalMarginHeaderConst,
} from 'constants/numericalValues';
import { IShowOptions, DESCRIPTION } from './types';
import {
  ArrowButton,
  ArrowContainer,
  CloseButton,
  CustomerText,
  DealDetails,
  DescriptionBorder,
  DescriptionText,
  DescriptionValue,
  DescriptionWhiteValue,
  GrayBackground,
  PaddingBox,
  PeriodText,
  SmallFontText,
  SmallText,
  StatusValueText,
  StyledLink,
  SubHeaderData,
  TableDataContent,
  WeightHeader,
} from '../components';

export const RenderDealEditView = ({
  index,
  shiftDealLeft,
  deleteDeal,
  shiftDealRight,
  dealsWithEvaluation,
}: {
  index: number;
  shiftDealLeft: (index: number) => void;
  deleteDeal: (index: number) => void;
  shiftDealRight: (index: number) => void;
  dealsWithEvaluation: IDealWithEvaluation[];
}) => {
  return (
    <td>
      <ArrowContainer>
        <ArrowButton
          data-testid="shift-deal-left-button"
          type="button"
          disabled={index === 0}
          onClick={() => shiftDealLeft(index)}
        >
          <MuiChevronLeft />
        </ArrowButton>
        <CloseButton data-testid="remove-deal-button" type="button" onClick={() => deleteDeal(index)}>
          <MuiClose />
        </CloseButton>
        <ArrowButton
          data-testid="shift-deal-right-button"
          type="button"
          disabled={index === dealsWithEvaluation.length - 1}
          onClick={() => shiftDealRight(index)}
        >
          <MuiChevronRight />
        </ArrowButton>
      </ArrowContainer>
    </td>
  );
};

export const RenderDealDetails = ({
  customer,
  id,
  name,
  products,
  status,
}: {
  customer?: string;
  id?: string;
  name: string;
  products?: IDealProduct[];
  status: string;
}) => {
  return (
    <DealDetails key={uuid()}>
      <TableDataContent>
        <CustomerText>{customer}</CustomerText>
        <StyledLink to={`${routes.deals}/${id}`}>{name}</StyledLink>
        <PaddingBox>
          <SmallText>Period</SmallText>
          <div>
            <PeriodText>{generateContractPeriodString(products)}</PeriodText>
            <span>({calculateNumOfMonths(products)} mo)</span>
          </div>
        </PaddingBox>
        {status !== 'other' && (
          <PaddingBox>
            <SmallFontText>Status</SmallFontText>
            <StatusValueText>{status}</StatusValueText>
          </PaddingBox>
        )}
      </TableDataContent>
    </DealDetails>
  );
};

export const RenderShowOptions = ({
  showOptions,
  dealsData,
  headers,
  currencyByWeightLabel,
}: {
  showOptions: IShowOptions;
  dealsData: IDealDataSelector[];
  headers: {
    header: string;
    subHeader: string[];
  }[];
  currencyByWeightLabel: string;
}) => {
  return (
    <>
      {showOptions.cap && (
        <>
          <tr>
            <SubHeaderData>
              {headers[optionsValueHeaderConst].subHeader[0]} <WeightHeader>({currencyByWeightLabel})</WeightHeader>
            </SubHeaderData>
            {dealsData.map(({ cap }: IDealDataSelector) => (
              <GrayBackground key={uuid()}>{cap}</GrayBackground>
            ))}
          </tr>
          <tr>
            <DescriptionText>{DESCRIPTION}</DescriptionText>
            {dealsData.map(({ capDescriptions }: IDealDataSelector) => (
              <DescriptionValue key={uuid()}>
                {capDescriptions?.map((cd: string) => (
                  <p key={uuid()}>{cd}</p>
                ))}
              </DescriptionValue>
            ))}
          </tr>
        </>
      )}
      {showOptions.floor && (
        <>
          <tr>
            <SubHeaderData>
              {headers[optionsValueHeaderConst].subHeader[1]}
              <WeightHeader>({currencyByWeightLabel})</WeightHeader>
            </SubHeaderData>

            {dealsData.map(({ floor }: IDealDataSelector) => (
              <td key={uuid()}>{floor}</td>
            ))}
          </tr>
          <tr>
            <DescriptionText>{DESCRIPTION}</DescriptionText>
            {dealsData.map(({ floorDescriptions }: IDealDataSelector) => (
              <DescriptionWhiteValue key={uuid()}>
                {floorDescriptions?.map((fd: string) => (
                  <p key={uuid()}>{fd}</p>
                ))}
              </DescriptionWhiteValue>
            ))}
          </tr>
        </>
      )}
      {showOptions.customerFlex && (
        <>
          <tr>
            <SubHeaderData>
              {headers[optionsValueHeaderConst].subHeader[pricingFormulaHeaderConst]}
              <WeightHeader>({currencyByWeightLabel})</WeightHeader>
            </SubHeaderData>

            {dealsData.map(({ customerFlex }: IDealDataSelector) => (
              <GrayBackground key={uuid()}>{customerFlex}</GrayBackground>
            ))}
          </tr>
          <tr>
            <DescriptionText>{DESCRIPTION}</DescriptionText>
            {dealsData.map(({ customerFlexDescription }: IDealDataSelector) => (
              <DescriptionValue key={uuid()}>{customerFlexDescription}</DescriptionValue>
            ))}
          </tr>
        </>
      )}
      {showOptions.pushVal && (
        <>
          <tr>
            <SubHeaderData>
              {headers[optionsValueHeaderConst].subHeader[volumeLabelHeaderConst]}
              <WeightHeader>({currencyByWeightLabel})</WeightHeader>
            </SubHeaderData>

            {dealsData.map(({ pushVal }: IDealDataSelector) => (
              <td key={uuid()}>{pushVal}</td>
            ))}
          </tr>
          <tr>
            <DescriptionText>{DESCRIPTION}</DescriptionText>
            {dealsData.map(({ pushDescription }: IDealDataSelector) => (
              <DescriptionWhiteValue key={uuid()}>
                {pushDescription?.map((pd: string) => (
                  <p key={uuid()}>{pd}</p>
                ))}
              </DescriptionWhiteValue>
            ))}
          </tr>
        </>
      )}
      {showOptions.pullVal && (
        <>
          <tr>
            <SubHeaderData>
              {headers[optionsValueHeaderConst].subHeader[currencyDollarHeaderConst]}
              <WeightHeader>({currencyByWeightLabel})</WeightHeader>
            </SubHeaderData>

            {dealsData.map(({ pullVal }: IDealDataSelector) => (
              <GrayBackground key={uuid()}>{pullVal}</GrayBackground>
            ))}
          </tr>
          <tr>
            <DescriptionText>{DESCRIPTION}</DescriptionText>
            {dealsData.map(({ pullDescription }: IDealDataSelector) => (
              <DescriptionValue key={uuid()}>
                {pullDescription?.map((pullD: string) => (
                  <p key={uuid()}>{pullD}</p>
                ))}
              </DescriptionValue>
            ))}
          </tr>
        </>
      )}
      {showOptions.extensions && (
        <>
          <tr>
            <SubHeaderData>
              {headers[optionsValueHeaderConst].subHeader[totalRevenueHeaderConst]}{' '}
              <WeightHeader>({currencyByWeightLabel})</WeightHeader>
            </SubHeaderData>

            {dealsData.map(({ extensions }: IDealDataSelector) => (
              <td key={uuid()}>{extensions}</td>
            ))}
          </tr>
          <tr>
            <DescriptionText>{DESCRIPTION}</DescriptionText>
            {dealsData.map(({ extensionDescription }: IDealDataSelector) => (
              <DescriptionWhiteValue key={uuid()}>
                {extensionDescription?.map((ed: string) => (
                  <p key={uuid()}>{ed}</p>
                ))}
              </DescriptionWhiteValue>
            ))}
          </tr>
        </>
      )}
      {showOptions.earlyTermination && (
        <>
          <tr>
            <SubHeaderData>
              {headers[optionsValueHeaderConst].subHeader[totalNetbackHeaderConst]}{' '}
              <WeightHeader>({currencyByWeightLabel})</WeightHeader>
            </SubHeaderData>
            {dealsData.map(({ earlyTermination }: IDealDataSelector) => (
              <GrayBackground key={uuid()}>{earlyTermination}</GrayBackground>
            ))}
          </tr>
          <tr>
            <DescriptionText>{DESCRIPTION}</DescriptionText>
            {dealsData.map(({ earlyTerminationDescription }: IDealDataSelector) => (
              <DescriptionValue key={uuid()}>{earlyTerminationDescription}</DescriptionValue>
            ))}
          </tr>
        </>
      )}
      {showOptions.priceSwitch && (
        <>
          <tr>
            <SubHeaderData>
              {headers[optionsValueHeaderConst].subHeader[totalMarginHeaderConst]}{' '}
              <WeightHeader>({currencyByWeightLabel})</WeightHeader>
            </SubHeaderData>
            {dealsData.map(({ priceSwitch }: IDealDataSelector) => (
              <td key={uuid()}>
                {priceSwitch?.map((pdt: string) => (
                  <p key={uuid()}>{pdt || '-'}</p>
                ))}
              </td>
            ))}
          </tr>
          <tr>
            <DescriptionText>{DESCRIPTION}</DescriptionText>
            {dealsData.map(({ priceSwitchDescription }: IDealDataSelector) => (
              <DescriptionBorder key={uuid()}>{priceSwitchDescription}</DescriptionBorder>
            ))}
          </tr>
        </>
      )}
    </>
  );
};
